
/* File: socket.js
 * Path: ananunadm/src/helpers/socket.js
*/
import React, { createContext, useContext, useEffect, useState } from "react";
import { io } from "socket.io-client";
import config from "@/config";
import { useUser } from "./userContext";
import { cookies } from "./cookies";
import { getOrCreateDeviceId } from "./deviceFingerprint";
import * as API from "./api";
import { getTenantKey } from "./tenant";
import { sameNotification } from "./notificationIdentity";

// Matches the attendance device/FlexUp punch sync job's own cadence
// (DEFAULT_PUNCH_SYNC_INTERVAL_MS in attendanceSyncJobConfig.service.js) —
// polling the derived "who's currently in office" signal faster than the
// underlying punch data can change is pointless.
const IN_OFFICE_POLL_INTERVAL_MS = 60000;

let socket = null;

export function getSocket() {
	if (!socket) {
		// Use config.api.API_URL - socket.io needs full URL (ensure protocol for ws)
		let apiUrl = config.api?.API_URL || "http://localhost:3000";
		if (apiUrl && apiUrl.startsWith("//")) {
			apiUrl = `${window?.location?.protocol || "http:"}${apiUrl}`;
		}
		socket = io(apiUrl, {
			transports: ["polling", "websocket"],
			reconnection: true,
			reconnectionDelay: 1000,
			reconnectionDelayMax: 5000,
			// An API restart outlasts five capped attempts; giving up leaves chat realtime dead until reload.
			reconnectionAttempts: Infinity,
			auth: (callback) =>
				callback({
					token: cookies.get(config.api.tokenAddress),
					deviceId: getOrCreateDeviceId(),
					tenant: getTenantKey()
				})
		});

		socket.on("connect", () => {
			// console.log("Admin Socket connected:", socket.id);
		});

		socket.on("disconnect", () => {
			// console.log("Admin Socket disconnected");
		});

		socket.on("connect_error", (error) => {
			// console.error("Admin Socket connection error:", error);
		});

		socket.on("forceLogout", (payload) => {
			window.dispatchEvent(
				new CustomEvent("apit:force-logout", {
					detail: payload || {}
				})
			);
		});
	}

	return socket;
}

export function disconnectSocket() {
	if (socket) {
		socket.disconnect();
		socket = null;
	}
}

// Socket Context
const SocketContext = createContext(null);

// Socket Provider Component
export function SocketProvider({ children }) {
	const { user } = useUser();
	const [socketInstance, setSocketInstance] = useState(null);
	const [myNotifications, setMyNotifications] = useState([]);
	const [connectedUsers, setConnectedUsers] = useState([]);
	const [inOfficeUserIds, setInOfficeUserIds] = useState(new Set());

	useEffect(() => {
		if (Array.isArray(user?.notifications)) {
			setMyNotifications(user.notifications);
		}
	}, [user?._id, user?.notifications]);

	useEffect(() => {
		const socket = getSocket();
		setSocketInstance(socket);

		// Listen for new notifications
		const handleNewNotification = (notification) => {
			try {
				const parsedNotification =
					typeof notification === "string" ? JSON.parse(notification) : notification;

				setMyNotifications((prev) => {
					const matchIndex = prev.findIndex((notif) =>
						sameNotification(notif, parsedNotification)
					);
					if (matchIndex !== -1) {
						const next = [...prev];
						next[matchIndex] = parsedNotification;
						return next;
					}

					// Add new notification to the beginning
					return [parsedNotification, ...prev];
				});
			} catch (error) {
				console.error("Error parsing notification:", error);
			}
		};

		// Listen for notification updates — keep in list, just update read state
		const handleNotificationRead = (notification) => {
			try {
				const parsedNotification =
					typeof notification === "string" ? JSON.parse(notification) : notification;

				setMyNotifications((prev) =>
					prev.map((notif) =>
						notif._id === parsedNotification._id ? parsedNotification : notif
					)
				);
			} catch (error) {
				console.error("Error parsing notification update:", error);
			}
		};

		// Notification removal is disabled — notifications are kept as permanent history
		const handleNotificationRemoved = () => {};

		// Listen for connected users updates
		const handleConnectedUsers = (users) => {
			if (Array.isArray(users)) {
				setConnectedUsers(users);
			}
		};

		if (socket) {
			socket.on("newNotification", handleNewNotification);
			socket.on("notificationRead", handleNotificationRead);
			socket.on("notificationRemoved", handleNotificationRemoved);
			socket.on("getConnectedUsers", handleConnectedUsers);
		}

		return () => {
			if (socket) {
				socket.off("newNotification", handleNewNotification);
				socket.off("notificationRead", handleNotificationRead);
				socket.off("notificationRemoved", handleNotificationRemoved);
				socket.off("getConnectedUsers", handleConnectedUsers);
			}
		};
	}, []);

	useEffect(() => {
		if (!socketInstance || !user?._id) return;

		const registerCurrentUser = () => {
			socketInstance.emit("registerUser", user, socketInstance.id);
		};

		if (socketInstance.connected) {
			registerCurrentUser();
		}

		socketInstance.on("connect", registerCurrentUser);

		return () => {
			socketInstance.off("connect", registerCurrentUser);
		};
	}, [socketInstance, user]);

	useEffect(() => {
		if (!socketInstance || !user?._id) return;
		const refreshPresence = () => {
			if (document.hidden) return;
			if (!socketInstance.connected) {
				socketInstance.connect();
				return;
			}
			socketInstance.emit("registerUser", user, socketInstance.id);
			socketInstance.emit("requestConnectedUsers");
		};
		const onVisibilityChange = () => {
			if (document.hidden) {
				if (window.matchMedia?.("(max-width: 767px)")?.matches && socketInstance.connected) {
					socketInstance.emit("userBackground");
				}
				return;
			}
			refreshPresence();
		};
		document.addEventListener("visibilitychange", onVisibilityChange);
		window.addEventListener("focus", refreshPresence);
		window.addEventListener("pageshow", refreshPresence);
		return () => {
			document.removeEventListener("visibilitychange", onVisibilityChange);
			window.removeEventListener("focus", refreshPresence);
			window.removeEventListener("pageshow", refreshPresence);
		};
	}, [socketInstance, user]);

	useEffect(() => {
		if (!user?._id) return;

		let cancelled = false;

		const fetchInOfficeUserIds = () => {
			API.get({ route: config.api.hr.attendanceCurrentlyInOffice })
				.then((response) => {
					if (cancelled || !Array.isArray(response?.inOfficeUserIds)) return;
					setInOfficeUserIds(new Set(response.inOfficeUserIds.map(String)));
				})
				.catch(() => {});
		};

		fetchInOfficeUserIds();
		const intervalId = setInterval(fetchInOfficeUserIds, IN_OFFICE_POLL_INTERVAL_MS);

		return () => {
			cancelled = true;
			clearInterval(intervalId);
		};
	}, [user?._id]);

	const setNewNotification = (notification) => {
		setMyNotifications((prev) => {
			const exists = prev.some((notif) => sameNotification(notif, notification));
			if (exists) return prev;

			// Add new notification to the beginning
			return [notification, ...prev];
		});
	};

	const createNotification = (notification) => {
		if (!socketInstance || !notification?.targetUsers?.length) return;
		socketInstance.emit("createNotification", JSON.stringify(notification));
	};

	const contextValue = {
		socket: socketInstance,
		myNotifications,
		setNewNotification,
		createNotification,
		setMyNotifications,
		connectedUsers,
		inOfficeUserIds
	};

	return <SocketContext.Provider value={contextValue}>{children}</SocketContext.Provider>;
}

// Hook to use socket in components
export function useSocket() {
	const context = useContext(SocketContext);

	if (!context) {
		// Return default values if context is not available (shouldn't happen if SocketProvider is used)
		return {
			socket: null,
			myNotifications: [],
			setNewNotification: () => {},
			createNotification: () => {},
			setMyNotifications: () => {},
			connectedUsers: [],
			inOfficeUserIds: new Set()
		};
	}

	return context;
}
